import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { type User } from '../../services/authService';
import { ENV } from '../../config/envConfig';

export type Environment = 'sandbox' | 'production';

const API_URLS = {
  sandbox: ENV.API_BASE_URL_SANDBOX,
  production: ENV.API_BASE_URL_PRODUCTION,
};

const COMPANY_URLS = {
  sandbox: ENV.COMPANY_API_URL_SANDBOX,
  production: ENV.COMPANY_API_URL_PRODUCTION,
};

// Define slice state interface
interface AuthState {
  user: User | null;
  loginUser: User | null; // Original login user - never changes when switching companies
  pendingUser: User | null; // Temporary storage before OTP verification
  pendingToken: string | null; // Temporary token storage before OTP verification
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  // Environment management
  environment: Environment;
  apiBaseUrl: string;
  currentApiKey: string | null;
  companyBaseUrl: string;
  forgotPassword: {
    loading: boolean;
    message: string | null;
    error: string | null;
  };
  resetPassword: {
    loading: boolean;
    success: boolean;
    message: string | null;
    error: string | null;
  };
  updatePassword: {
    loading: boolean;
    success: boolean;
    message: string | null;
    error: string | null;
  };
  verifyOtp: {
    loading: boolean;
    success: boolean;
    error: string | null;
    verifyData: any | null;
  };
  resendOtp: {
    loading: boolean;
    success: boolean;
    error: string | null;
  };
  sendOtp: {
    loading: boolean;
    success: boolean;
    error: string | null;
  };
}

// Define initial state
const initialState: AuthState = {
  user: null,
  loginUser: null,
  pendingUser: null,
  pendingToken: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  // Environment defaults
  environment: 'sandbox',
  apiBaseUrl: API_URLS.sandbox,
  currentApiKey: null,
  companyBaseUrl: COMPANY_URLS.sandbox,
  forgotPassword: {
    loading: false,
    message: null,
    error: null,
  },
  resetPassword: {
    loading: false,
    success: false,
    message: null,
    error: null,
  },
  updatePassword: {
    loading: false,
    success: false,
    message: null,
    error: null,
  },
  verifyOtp: {
    loading: false,
    success: false,
    error: null,
    verifyData: null,
  },
  resendOtp: {
    loading: false,
    success: false,
    error: null,
  },
  sendOtp: {
    loading: false,
    success: false,
    error: null,
  },
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Login actions
    loginRequest: (
      state,
      _action: PayloadAction<{ email: string; password: string }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        accessToken: string;
        refreshToken: string;
      }>
    ) => {
      state.loading = false;
      state.user = action.payload.user;
      state.loginUser = action.payload.user; // Store original login user separately
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.error = null;
      // Clear pending data after successful authentication
      state.pendingUser = null;
      state.pendingToken = null;
      // Set current API key based on environment
      state.currentApiKey = state.environment === 'sandbox'
        ? action.payload.user.accountId || null
        : action.payload.user.prodAccountId || null;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = action.payload;
    },
    // Login pending (before OTP verification)
    loginPending: (
      state,
      action: PayloadAction<{ user: User; token: string }>
    ) => {
      state.loading = false;
      state.pendingUser = action.payload.user;
      state.pendingToken = action.payload.token;
      state.error = null;
      // Don't set user, tokens, or isAuthenticated yet
    },
    // Refresh token actions
    refreshTokenRequest: state => {
      state.loading = true;
      state.error = null;
    },
    // Fetch user profile actions
    fetchUserProfileRequest: state => {
      state.loading = true;
      state.error = null;
    },
    // Forgot password actions
    forgotPasswordRequest: (state, _action: PayloadAction<{ email: string; type: string }>) => {
      state.forgotPassword.loading = true;
      state.forgotPassword.error = null;
      state.forgotPassword.message = null;
    },
    forgotPasswordSuccess: (state, action: PayloadAction<string>) => {
      state.forgotPassword.loading = false;
      state.forgotPassword.message = action.payload;
      state.forgotPassword.error = null;
    },
    forgotPasswordFailure: (state, action: PayloadAction<string>) => {
      state.forgotPassword.loading = false;
      state.forgotPassword.message = null;
      state.forgotPassword.error = action.payload;
    },
    // Reset password actions
    resetPasswordRequest: (state, _action: PayloadAction<{ id: string; password: string }>) => {
      state.resetPassword.loading = true;
      state.resetPassword.error = null;
      state.resetPassword.success = false;
    },
    resetPasswordSuccess: (state, action: PayloadAction<string>) => {
      state.resetPassword.loading = false;
      state.resetPassword.success = true;
      state.resetPassword.message = action.payload;
      state.resetPassword.error = null;
    },
    resetPasswordFailure: (state, action: PayloadAction<string>) => {
      state.resetPassword.loading = false;
      state.resetPassword.success = false;
      state.resetPassword.message = null;
      state.resetPassword.error = action.payload;
    },
    resetResetPasswordState: (state) => {
      state.resetPassword.loading = false;
      state.resetPassword.success = false;
      state.resetPassword.error = null;
      state.resetPassword.message = null;
    },
    // Update password actions
    updatePasswordRequest: (state, _action: PayloadAction<{ id: string; password: string }>) => {
      state.updatePassword.loading = true;
      state.updatePassword.error = null;
      state.updatePassword.message = null;
      state.updatePassword.success = false;
    },
    updatePasswordSuccess: (state, action: PayloadAction<string>) => {
      state.updatePassword.loading = false;
      state.updatePassword.success = true;
      state.updatePassword.message = action.payload;
      state.updatePassword.error = null;
    },
    updatePasswordFailure: (state, action: PayloadAction<string>) => {
      state.updatePassword.loading = false;
      state.updatePassword.success = false;
      state.updatePassword.error = action.payload;
    },
    resetUpdatePasswordState: (state) => {
      state.updatePassword.loading = false;
      state.updatePassword.success = false;
      state.updatePassword.error = null;
      state.updatePassword.message = null;
    },
    // Verify OTP actions
    verifyOtpRequest: (state, _action: PayloadAction<{ email: string; code: string }>) => {
      state.verifyOtp.loading = true;
      state.verifyOtp.success = false;
      state.verifyOtp.error = null;
    },
    verifyOtpSuccess: (state, action: PayloadAction<any>) => {
      state.verifyOtp.loading = false;
      state.verifyOtp.success = true;
      state.verifyOtp.verifyData = action.payload;
      state.verifyOtp.error = null;
    },
    verifyOtpFailure: (state, action: PayloadAction<string>) => {
      state.verifyOtp.loading = false;
      state.verifyOtp.success = false;
      state.verifyOtp.error = action.payload;
    },
    resetVerifyOtpState: (state) => {
      state.verifyOtp.loading = false;
      state.verifyOtp.success = false;
      state.verifyOtp.error = null;
      state.verifyOtp.verifyData = null;
    },
    // Resend OTP actions
    resendOtpRequest: (state, _action: PayloadAction<{ email: string }>) => {
      state.resendOtp.loading = true;
      state.resendOtp.success = false;
      state.resendOtp.error = null;
    },
    resendOtpSuccess: (state) => {
      state.resendOtp.loading = false;
      state.resendOtp.success = true;
      state.resendOtp.error = null;
    },
    resendOtpFailure: (state, action: PayloadAction<string>) => {
      state.resendOtp.loading = false;
      state.resendOtp.success = false;
      state.resendOtp.error = action.payload;
    },
    resetResendOtpState: (state) => {
      state.resendOtp.loading = false;
      state.resendOtp.success = false;
      state.resendOtp.error = null;
    },
    // Send OTP actions
    sendOtpRequest: (state, _action: PayloadAction<{ email: string }>) => {
      state.sendOtp.loading = true;
      state.sendOtp.success = false;
      state.sendOtp.error = null;
    },
    sendOtpSuccess: (state) => {
      state.sendOtp.loading = false;
      state.sendOtp.success = true;
      state.sendOtp.error = null;
    },
    sendOtpFailure: (state, action: PayloadAction<string>) => {
      state.sendOtp.loading = false;
      state.sendOtp.success = false;
      state.sendOtp.error = action.payload;
    },
    resetSendOtpState: (state) => {
      state.sendOtp.loading = false;
      state.sendOtp.success = false;
      state.sendOtp.error = null;
    },
    // Set company as authenticated user (for company selection flow)
    setCompanyAsUser: (
      state,
      action: PayloadAction<{
        accountId: string | null;
        prodAccountId?: string | null;
        email: string;
        firstName: string;
        lastName: string;
        companyName: string;
        phone?: string;
        address?: string;
        businessType?: string[];
        selectedFeatures?: string[];
        isActivated?: boolean;
        isoLicense?: string;
        numberOfMerchants?: number | null;
        comments?: string;
        isApproved?: boolean;
        approvedBy?: string;
      }>
    ) => {
      // COMPLETELY REPLACE user data with company data (preserve role and userType from existing state)
      state.user = {
        id: action.payload.accountId || action.payload.prodAccountId || '',
        accountId: action.payload.accountId || undefined,
        prodAccountId: action.payload.prodAccountId || undefined,
        email: action.payload.email,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        role: state.user?.role || '',                // Preserve existing role from state, empty if not present
        userType: state.user?.userType || '',        // Preserve existing userType from state, empty if not present
        phone: action.payload.phone || '',
        profile: action.payload.selectedFeatures || ['developerDocs', 'enrich_menu'],
        // Company-specific fields
        companyName: action.payload.companyName,
        address: action.payload.address,
        businessType: action.payload.businessType,
        isActivated: action.payload.isActivated,
        isoLicense: action.payload.isoLicense,
        numberOfMerchants: action.payload.numberOfMerchants,
        comments: action.payload.comments,
        isApproved: action.payload.isApproved,
        approvedBy: action.payload.approvedBy,
      };
      
      // Update currentApiKey based on environment
      state.currentApiKey = state.environment === 'sandbox'
        ? action.payload.accountId || null
        : action.payload.prodAccountId || null;
      
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;
    },
    // Set environment action
    setEnvironment: (state, action: PayloadAction<Environment>) => {
      state.environment = action.payload;
      state.apiBaseUrl = API_URLS[action.payload];
      state.companyBaseUrl = COMPANY_URLS[action.payload];
      // Update current API key based on new environment
      state.currentApiKey = action.payload === 'sandbox'
        ? state.user?.accountId || null
        : state.user?.prodAccountId || null;
    },
    // Logout action
    logout: state => {
      state.user = null;
      state.loginUser = null; // Clear login user on logout
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      // Reset environment to default
      state.environment = 'sandbox';
      state.apiBaseUrl = API_URLS.sandbox;
      state.companyBaseUrl = COMPANY_URLS.sandbox;
      state.currentApiKey = null;
      state.forgotPassword = {
        loading: false,
        message: null,
        error: null,
      };
      state.resetPassword = {
        loading: false,
        success: false,
        message: null,
        error: null,
      };
      state.updatePassword = {
        loading: false,
        success: false,
        message: null,
        error: null,
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(REHYDRATE, (state, action: any) => {
      if (action.payload && action.payload.auth) {
        const rehydratedAuth = action.payload.auth;
        if (rehydratedAuth) {
          // Restore auth state from persistence
          state.user = rehydratedAuth.user;
          state.loginUser = rehydratedAuth.loginUser; // Restore login user
          state.accessToken = rehydratedAuth.accessToken;
          state.refreshToken = rehydratedAuth.refreshToken;
          state.isAuthenticated = rehydratedAuth.isAuthenticated;
          // Restore environment state
          state.environment = rehydratedAuth.environment || 'sandbox';
          state.apiBaseUrl = rehydratedAuth.apiBaseUrl || API_URLS.sandbox;
          state.companyBaseUrl = rehydratedAuth.companyBaseUrl || COMPANY_URLS.sandbox;
          state.currentApiKey = rehydratedAuth.currentApiKey || null;
          // Don't restore loading/error states
          state.loading = false;
          state.error = null;
          // Initialize forgotPassword state
          state.forgotPassword = {
            loading: false,
            message: null,
            error: null,
          };
          // Initialize resetPassword state
          state.resetPassword = {
            loading: false,
            success: false,
            message: null,
            error: null,
          };
          // Initialize updatePassword state
          state.updatePassword = {
            loading: false,
            success: false,
            message: null,
            error: null,
          };
        }
      }
    });
  },
});

// Export actions
export const {
  loginRequest,
  loginSuccess,
  loginPending,
  loginFailure,
  refreshTokenRequest,
  fetchUserProfileRequest,
  forgotPasswordRequest,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPasswordRequest,
  resetPasswordSuccess,
  resetPasswordFailure,
  resetResetPasswordState,
  updatePasswordRequest,
  updatePasswordSuccess,
  updatePasswordFailure,
  resetUpdatePasswordState,
  verifyOtpRequest,
  verifyOtpSuccess,
  verifyOtpFailure,
  resetVerifyOtpState,
  resendOtpRequest,
  resendOtpSuccess,
  resendOtpFailure,
  resetResendOtpState,
  sendOtpRequest,
  sendOtpSuccess,
  sendOtpFailure,
  resetSendOtpState,
  setCompanyAsUser,
  setEnvironment,
  logout,
} = authSlice.actions;

export default authSlice.reducer;
